import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import React, { useCallback, useState } from 'react';

import { Alert, Box, Loader, Text } from '~/components/blocks';
import { Document, Page } from '~/utils/pdfjs';
import { isIpad } from '~/utils/user_agent';

type Props = {
  src: string;
  onError: () => void;
  onLoad: () => void;
};

const LoaderWrapper = styled('div')(({ theme }) =>
  css({
    backgroundColor: theme.colors.background.bg,
    height: '200px',
    position: 'relative',
  }),
);

// react-pdfのスタイル指定が数値のみだったりで直接指定した
// 無理矢理感が否めないのでいい方法があれば書き換え
const Wrapper = styled('div')(({ theme }) =>
  css({
    '& .react-pdf__Document': {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(50%, 1fr))`,
    },
    '& .dispensing_request': {
      width: '100%',
      marginY: theme.space.m,
      padding: theme.space.m,
      overflow: 'hidden',
      transitionDuration: theme.transitions.default,
      '& + &': {
        marginTop: theme.space.m,
      },
      '& > .react-pdf__Page__canvas': {
        width: '75% !important',
        height: 'auto !important',
        margin: '0 auto',
        '& > canvas': {
          width: '100% !important',
          height: 'auto !important',
        },
      },
    },
  }),
);

interface PDFDocumentProxy {
  numPages: number;
}

export const PdfViewer = (props: Props) => {
  const { onLoad } = props;

  const theme = useTheme();

  const [numPages, setNumPages] = useState<number>(0);
  const handleLoaded = useCallback(
    (pdf: PDFDocumentProxy) => {
      onLoad();

      // TODO: メモリリーク対策
      setNumPages(pdf.numPages);
    },
    [onLoad],
  );

  return (
    <Wrapper>
      <Document
        loading={
          <LoaderWrapper>
            <Loader open inside logoSize="half" />
          </LoaderWrapper>
        }
        error={
          <Box padding={theme.space.m}>
            <Alert>
              <Text size="s">
                ファイル取得中にエラーが発生しました。
                <br />
                しばらく待ってから、再度アクセスしてください
              </Text>
            </Alert>
          </Box>
        }
        file={props.src}
        renderMode="canvas"
        onLoadError={props.onError}
        onLoadSuccess={handleLoaded}
        onSourceError={props.onError}
      >
        {Array.from({ length: numPages }, (_, i) => {
          return (
            <Page
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="dispensing_request"
              key={i}
              pageNumber={i + 1}
              scale={2}
              // iPadの場合はdevicePixelRatioを1にする
              // ref. https://github.com/wojtekmaj/react-pdf/issues/1149#issuecomment-1323541137
              devicePixelRatio={isIpad() ? 1 : window.devicePixelRatio}
            />
          );
        })}
      </Document>
    </Wrapper>
  );
};
